import { h } from '@omni-torn/ui';
import { TICKET_STATUS_FOR_REPORTER, type TicketEvent, type TicketReply, type TicketView } from '@omni-torn/shared';
import { server } from '../session';
import { tct } from '../ui';

const WHO: Record<TicketReply['role'], string> = { owner: 'Omni-Torn', reporter: 'You', dev: 'Developer' };
const DONE: readonly TicketView['status'][] = ['closed', 'declined'];

/**
 * Account → Your reports (owner's call 2026-10-22, 181050-idea-2282863-6a9a: reports are tickets): the bug reports and
 * suggestions you sent from Torn's pages, where each stands, the whole history (every answer and change of status —
 * 152952-idea-2282863-37e8), a "Test it here" link once fixed (152307-idea-2282863-59ba), and a box to reply. Done ones
 * can be hidden (043042-idea-3897780-fde0; a new answer shows it again). Only your own (/api/tickets/mine answers with
 * nothing else).
 */
export function reportsBox(): HTMLElement {
  const body = h('div', { class: 'tickets' }, h('p', { class: 'muted' }, 'Loading your reports…'));
  let showHidden = false;
  const draw = async () => {
    let ts: TicketView[];
    try {
      ts = (await server<{ tickets: TicketView[] }>('GET', '/api/tickets/mine')).tickets ?? [];
    } catch (err) {
      body.replaceChildren(h('p', { class: 'notice bad' }, (err as Error).message));
      return;
    }
    if (!ts.length) {
      body.replaceChildren(h('p', { class: 'muted' }, 'None yet. Send one with “🎫 Tickets” (Report a bug / Suggest) at the top of any Torn page (the Omni-Torn extension or Torn PDA) — the answers show here.'));
      return;
    }
    const hidden = ts.filter((t) => t.hidden && DONE.includes(t.status));
    const shown = ts.filter((t) => !hidden.includes(t));
    const more = hidden.length ? h('button', { type: 'button', class: 'btn-small ticket-show-hidden' }, showHidden ? 'Hide the closed ones again' : `Show ${hidden.length} hidden`) : null;
    more?.addEventListener('click', () => {
      showHidden = !showHidden;
      void draw();
    });
    body.replaceChildren(...(more ? [h('p', {}, more)] : []), ...[...shown, ...(showHidden ? hidden : [])].map((t) => item(t, draw)));
  };
  void draw();
  return h('section', { class: 'panel', id: 'reports' }, h('header', { class: 'panel-head' }, h('h2', {}, 'Your reports')), h('div', { class: 'panel-body' }, body));
}

/** A change on the timeline in words (the reporter's words for statuses). */
function change(e: TicketEvent): string {
  const s = (x?: string) => (x && x in TICKET_STATUS_FOR_REPORTER ? TICKET_STATUS_FOR_REPORTER[x as TicketView['status']] : x ?? '?');
  if (e.kind === 'pictures') return `${e.files?.length ?? 0} picture(s) added${e.text ? `: ${e.text}` : ''}`;
  if (e.kind === 'linked') return e.link === 'from' ? `Opened from an earlier report's follow-up${e.text ? ` — ${e.text}` : ''}` : `A follow-up was opened as a new report${e.text ? ` — ${e.text}` : ''}`;
  return e.kind === 'status' ? `Status: ${s(e.from)} → ${s(e.to)}${e.version ? ` (in ${e.version})` : ''}` : e.kind === 'priority' ? `Priority: ${e.from ?? '?'} → ${e.to ?? '?'}` : '';
}

function item(t: TicketView, redraw: () => Promise<void>): HTMLElement {
  const text = h('textarea', { rows: '2', maxlength: '2000', placeholder: 'Add something (more detail, “still happens”, thanks…)', 'aria-label': 'Your reply' }) as HTMLTextAreaElement;
  const status = h('span', { class: 'save-status', role: 'status' });
  const form = h('form', { class: 'ticket-reply' }, text, h('button', { type: 'submit', class: 'btn-small' }, 'Send'), status);
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!text.value.trim()) return;
    status.className = 'save-status';
    status.textContent = 'Sending…';
    try {
      await server('POST', '/api/tickets/reply', { id: t.id, text: text.value });
      await redraw();
    } catch (err) {
      status.className = 'save-status bad';
      status.textContent = (err as Error).message;
    }
  });
  const hide = DONE.includes(t.status) ? h('button', { type: 'button', class: 'btn-small ticket-hide' }, t.hidden ? 'Show again' : 'Hide') : null;
  hide?.addEventListener('click', async () => {
    try {
      await server('POST', '/api/tickets/hide', { id: t.id, hidden: !t.hidden });
      await redraw();
    } catch (err) {
      status.className = 'save-status bad';
      status.textContent = (err as Error).message;
    }
  });
  const events = t.timeline?.length ? t.timeline.filter((e) => e.kind !== 'opened' && e.kind !== 'note') : t.replies.map((r) => ({ ...r, kind: 'reply' as const }));
  return h(
    'div',
    { class: `ticket ticket-${t.status}`, 'data-id': t.id },
    h('p', {}, `${t.kind === 'bug' ? '🐞' : '💡'} `, h('strong', { class: 'ticket-status' }, TICKET_STATUS_FOR_REPORTER[t.status] + (t.status === 'testing' && t.fixedIn ? ` (${t.fixedIn})` : '')), h('span', { class: 'muted small' }, ` · ${t.title || t.url.split('?')[0]} · ${tct(t.at)} TCT`), ...(hide ? [' ', hide] : [])),
    h('p', { class: 'ticket-text' }, t.text),
    t.testUrl ? h('p', {}, h('a', { href: t.testUrl, target: '_blank', rel: 'noopener', class: 'ticket-test' }, '🔗 Test it here (on Torn)')) : null,
    events.length
      ? h(
          'ul',
          { class: 'ticket-thread' },
          ...events.map((e) =>
            e.kind === 'reply'
              ? h('li', {}, h('strong', {}, `${WHO[e.role]}: `), e.text ?? '', ...(e.url ? [' ', h('a', { href: e.url, target: '_blank', rel: 'noopener' }, '🔗 Test it here')] : []), h('span', { class: 'muted small' }, ` · ${tct(e.at)} TCT`))
              : h('li', { class: 'muted small' }, `↻ ${change(e)} · ${tct(e.at)} TCT`),
          ),
        )
      : null,
    form,
  );
}
